﻿import { ValidationModelErrors, ValidationModelError, SoftoolsValidationResult } from "../Results/softools-validation-result";
import each from 'lodash/each';
import flatten from 'lodash/flatten';
import map from 'lodash/map';
import axios from 'axios';
import { RedirectResult } from "../Results/redirect-result";
import * as parser from '@softools/odata-parser';
import SoftoolsGlobalStore from "../Store/softools-global-store";
import { some } from "lodash";

type AppStudioValidationRuleContext = {
    field?: string;
    label?: string;
    name?: string;
};

type AppStudioValidationRule<TValue = unknown, TParams = unknown[] | Record<string, unknown>> = (
    value: TValue,
    params: TParams,
    context: AppStudioValidationRuleContext
) => boolean | string | Promise<boolean | string>;

function validationFieldName(context: AppStudioValidationRuleContext): string {
    return context.label || context.name || context.field || 'This field';
}

function validationParameters(params: unknown[] | Record<string, unknown>, names: string[]): Record<string, unknown> {
    if (!Array.isArray(params)) {
        return params;
    }

    return Object.fromEntries(names.map((name, index) => [name, params[index]]));
}

export namespace ValidationHelper {
    export const failedSaveValidationMessage = 'Unable to save changes. Please check the form and try again.';

    export function highlightErrorsForFailedRequest(jqXHR: JQuery.jqXHR) {
        const softoolsValidationResult = readSoftoolsValidationResult(jqXHR);

        if (hasValidationErrors(softoolsValidationResult)) {
            ValidationHelper.highlightErrors(softoolsValidationResult.validationErrorsDict);
            return;
        }

        showFailedSaveMessage();
    }

    export function handleRedirectOrValidatedResult(result: RedirectResult | SoftoolsValidationResult) {
        try {
            const redirectResult = result as RedirectResult;
            if (redirectResult && redirectResult.redirect && (redirectResult.redirect as string).length > 0) {
                window.location.href = redirectResult.redirect as string;
                return;
            }

            const softoolsValidationResult = result as SoftoolsValidationResult;
            if (hasValidationErrors(softoolsValidationResult)) {
                ValidationHelper.highlightErrors(softoolsValidationResult.validationErrorsDict);
            } else if (softoolsValidationResult?.hasErrors === true) {
                showFailedSaveMessage();
            }
        } catch {
            // (Hopefully) caught by the generic error handler in `config.js`.
        }
    }

    export function highlightErrors(validationErrorsDict: Record<string, ValidationModelErrors>) {
        try {
            ValidationHelper.highlightFields(validationErrorsDict);
        } catch {
            // Field highlighting is best-effort. The summary must still give the user feedback.
        }

        try {
            ValidationHelper.showErrorSummary(validationErrorsDict);
        } catch {
            // (Hopefully) caught by the generic error handler in `config.js`.
        }

        try {
            window.scrollTo(0, 0);
        } catch {
            // (Hopefully) caught by the generic error handler in `config.js`.
        }
    }

    export function highlightFields(validationErrorsDict: Record<string, ValidationModelErrors>) {
        $('.form-group').removeClass('is-invalid');
        $('.is-invalid').removeClass('is-invalid');

        $.each(validationErrorsDict, (propName: string, val: ValidationModelErrors) => {
            const $el = findFieldElement(propName);

            if (val.errors.length > 0) {
                $el.addClass('is-invalid');

                each(val.errors,
                    function (error: ValidationModelError) {
                        $el.siblings('.invalid-feedback').remove();
                        $el.after($('<div class="invalid-feedback"></div>').text(error.errorMessage));
                    });
            }
        });
    }

    function findFieldElement(propName: string): JQuery<HTMLElement> {
        const propNameLower = propName.toLowerCase();
        const escapedPropName = propName.replace(/(:|\.|\[|\])/g, "\\$1");
        let $el = $('[name]').filter((_index, element) => {
            return ($(element).attr('name') ?? '').toLowerCase() === propNameLower;
        }) as JQuery<HTMLElement>;

        if ($el.length === 0) {
            $el = $(`#${escapedPropName}`) as JQuery<HTMLElement>;
        }

        return $el;
    }

    export function getSummaryErrorMessagesFromResponse(validationErrorsDict: Record<string, ValidationModelErrors>): string[] {
        const errorsMap = map(validationErrorsDict, function (o: ValidationModelErrors) { return o.errors });
        const verboseErrors: ValidationModelError[] = flatten(errorsMap);

        if (verboseErrors.length > 0) {
            const errorMessages: string[] = [];

            each(verboseErrors, function (error: ValidationModelError) {
                if (error.errorMessage && error.errorMessage.length > 0) {
                    errorMessages.push(error.errorMessage);
                }
            })

            if (errorMessages && errorMessages.length > 0) {
                return errorMessages;
            }
        }

        return [];
    }

    export function showErrorSummary(validationErrorsDict: Record<string, ValidationModelErrors>) {
        const errorMessages = getSummaryErrorMessagesFromResponse(validationErrorsDict);

        if (errorMessages && errorMessages.length > 0) {
            SoftoolsGlobalStore.getSummaryErrorsModule().setSummaryErrorMessages(errorMessages);
        }
    }

    export function addErrorSummaryMessage(errorMesage: string) {
        if (errorMesage && errorMesage.length > 0) {
            SoftoolsGlobalStore.getSummaryErrorsModule().addSummaryErrorMessage(errorMesage);
        }
    }

    export function addErrorSummaryMessages(validationErrorsDict: Record<string, ValidationModelErrors>) {
        const errorMessages = getSummaryErrorMessagesFromResponse(validationErrorsDict);

        if (errorMessages && errorMessages.length > 0) {

            errorMessages.forEach(o => {
                addErrorSummaryMessage(o);
            });
        }
    }

    export function clearErrorSummary() {
        SoftoolsGlobalStore.getSummaryErrorsModule().clearSummaryErrorMessages();
    }

    export function clearSummaryErrorMessagesThatContain(containsText: string) {
        SoftoolsGlobalStore.getSummaryErrorsModule().clearSummaryErrorMessagesThatContain(containsText);
    }

    export function showFailedSaveMessage(message = failedSaveValidationMessage) {
        SoftoolsGlobalStore.getSummaryErrorsModule().setSummaryErrorMessages([message]);
    }

    export function hasValidationErrors(result: SoftoolsValidationResult | undefined): result is SoftoolsValidationResult & { validationErrorsDict: Record<string, ValidationModelErrors> } {
        return !!result?.validationErrorsDict
            && Object.keys(result.validationErrorsDict).length > 0;
    }

    export function readSoftoolsValidationResult(jqXHR: JQuery.jqXHR): SoftoolsValidationResult | undefined {
        const responseJson = (jqXHR as JQuery.jqXHR & { responseJSON?: unknown }).responseJSON;
        if (responseJson) {
            return responseJson as SoftoolsValidationResult;
        }

        if (!jqXHR.responseText) {
            return undefined;
        }

        try {
            return JSON.parse(jqXHR.responseText) as SoftoolsValidationResult;
        } catch {
            return undefined;
        }
    }

    export function getUniqueIdentifierRule(uniqueIdentifierUrl: string) {
        return getUniqueRule(uniqueIdentifierUrl, 'identifier');
    }

    export function getUniqueNameRule(uniqueNameUrl: string) {
        return getUniqueRule(uniqueNameUrl, 'name');
    }

    export function getUniqueRule(uniqueUrl: string, queryParamKey: string): AppStudioValidationRule {
        return async (value, _params, context) => {
            const url = `${uniqueUrl}?${queryParamKey}=${value}`;

            try {
                const response = await axios.get(url);
                return response.data.valid
                    ? true
                    : response.data.message || `${validationFieldName(context)} is not valid as it's already in use`;
            }
            catch {
                return 'An error occurred validating the field';
            }
        };
    }

    export function uniqueValueRule(): AppStudioValidationRule {
        return (value, params, context) => {
            const { enabled, inUseValues } = validationParameters(params, ['enabled', 'inUseValues']);
            if (!enabled || !Array.isArray(inUseValues) || inUseValues.length === 0) {
                return true;
            }

            const normalizedValue = String(value).toLowerCase();
            const isBeingUsed = inUseValues.some(item => String(item).toLowerCase() === normalizedValue);
            return isBeingUsed
                ? `${validationFieldName(context)} is not valid as it's already in use`
                : true;
        };
    }

    export function getIsNotKeywordRule(): AppStudioValidationRule {
        return (value, _params, context) => {
            // Note we also have a server side keyword list in IdentifierValidator - we should merge these together.
            const keywords = ["Id", "_id", "AccessRights", "_AccessTokens", "Hierarchies", "Hierarchy",
                "NotificationGroups", "AppIdentifier", "IsArchived", "Tags",
                "AddInJiraLinkedIssue", "AddInJiraLinkedStatus", "ReadOnlyFields", "Identifier", "_Identifier",
                "CreatedByUserId", "CreatedByUser", "CreatedDate", "UpdatedByUserId",
                "UpdatedByUser", "UpdatedDate", "QuickFilterSearchText", "IconUri", "_CommentsCount", "_AttachmentsCount",
                "UserIdentifier", "LegacyId", "EditableAccessForUser",
                "sys_card", "ns_left", "ns_right"
            ];

            const normalizedValue = String(value).toLowerCase();
            const hasKeywordMatch = some(keywords, keyword => keyword.toLowerCase() === normalizedValue);
            return hasKeywordMatch
                ? `${validationFieldName(context)} is not valid as it's a reserved keyword`
                : true;
        };
    }

    export function getValidIdentifierRule(): AppStudioValidationRule {
        return value => {
            // Starts with a letter, allow any letters/ any numbers, max length 255 (Fields have the longest Identifier max length)
            // SOF-10700:  Regex should cater for Japanese Letters e.g. 整 etc - DO NOT use /^[a-zA-Z][a-zA-Z}\0-9]{0,254}$/u for example.
            const expression = /^[\p{L}][\p{L}\p{N}]{0,254}$/u;
            return typeof value === 'string' && expression.test(value)
                ? true
                : 'Identifiers can only contain letters and numbers and must start with a letter';
        };
    }

    export function getValidStyleNameRule(): AppStudioValidationRule {
        return value => {
            const expression = /^[\p{L}][\p{L}\p{N}_-]{0,}$/u;
            return typeof value === 'string' && expression.test(value)
                ? true
                : 'Style Names can only contain letters, numbers, underscores and dashes. Names must start with a letter';
        };
    }

    export function validateUrlRule(): AppStudioValidationRule {
        return (value, _params, context) => {
            const expression = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi;
            if (value === undefined || value === null || value === '') {
                return true;
            }

            return expression.test(String(value))
                ? true
                : `${validationFieldName(context)} is not a valid url`;
        };
    }

    /**
     * Validate OData filterstring.
     * Note we only support '$filter=' query param.
     */
    export function validateOdataRule(): AppStudioValidationRule<string | null | undefined> {
        return value => {
            if (value === undefined || value === null || value === '') {
                return true;
            }

            try {
                const tree = parser.parse(value);
                if (tree.error) {
                    return 'Invalid odata filter.';
                }

                // SOF-11806: Only support '$filter=' param in this validator.
                const unsupportedParams = Object.getOwnPropertyNames(tree)
                    .filter(param => param !== '$filter')
                    .join(', ');
                return unsupportedParams
                    ? `Invalid odata filter. Unsupported query param(s) entered; ${unsupportedParams}`
                    : true;
            } catch (error: unknown) {
                const message = error instanceof Error ? error.message : '';
                const friendlyMessage = message.replace(
                    'Expected "$", "$callback=", "$expand=", "$filter=", "$format=", "$inlinecount=", "$orderby=", "$select=", "$skip=" or "$top="',
                    'Expected "$", "$filter="');
                return `Invalid odata filter. ${friendlyMessage}`.trim();
            }
        };
    }

    /**
     * Regular expression to match email domain names, without label prefix. 
     * See Site Configure > IsPublic/ AdminDomains _adminEmailDomainRegex for details.
     */
    export function isValidPublicAdminEmailDomainRule(): AppStudioValidationRule<string> {
        return value => {
            // Note if modified also update _adminEmailDomainRegex in Site Config > IsPublic/ AdministratorDomains command handler.
            const domainRegex = /^(?=.{1,253}$)(?:(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?=.{1,63}$)(?:[a-z0-9]*[a-z][a-z0-9]*))$/i;
            return domainRegex.test(value)
                ? true
                : "Invalid admin email domain. Should be the domain part and be no more than 253 characters long e.g 'company.com'";
        };
    }

    export function validateWebhookHeaderNameRule(): AppStudioValidationRule {
        return (value, _params, context) => {
            // https://developers.cloudflare.com/rules/transform/request-header-modification/reference/header-format/
            // Header names can contain; Alphanumeric characters: a-z, A-Z, and 0-9. The following special characters: - and _
            return /^[A-Za-z0-9_-]+$/u.test(String(value))
                ? true
                : `${validationFieldName(context)} is not a valid header. Can contain characters; A-Z a-z 0-9 _ -`;
        };
    }

    export function validateWebhookBodyMemberNameRule(): AppStudioValidationRule {
        return (value, _params, context) => {
            // https://jsonapi.org/format/#document-member-names
            // Json member names can contain; Alphanumeric characters: a-z, A-Z, and 0-9. Additionally, the following characters are allowed in member names, except as the first or last character: - and _
            return /^[a-zA-Z0-9]+([-_][a-zA-Z0-9]+)*$/u.test(String(value))
                ? true
                : `${validationFieldName(context)} is not a valid key. Can contain characters; A-Z a-z 0-9 _ -. Special characters cannot be the first or last character`;
        };
    }

}
